import React, { useState, useEffect } from 'react';
import { onSnapshot, doc, setDoc, getDoc } from 'firebase/firestore';
import { useParams } from 'react-router-dom';
import { db } from '../../firebaseConfig';
import { runActiveLearning, previewActiveLearning } from '../../lib/callables';

interface ActiveLearningConfig {
  strategy: 'uncertainty' | 'class_balance';
  band: {
    min: number;
    max: number;
  };
  dailyBudget: number;
  recentBoostDays: number;
  enabled: boolean;
}

interface ActiveLearningLastRun {
  selected: number;
  enqueued: number;
  ranAt: any;
}

interface Candidate {
  submissionId: string;
  aiScore: number;
  age: number;
  reason: string;
  mediaUrl?: string;
}

interface Task {
  id: string;
  title: string;
}

const ActiveLearningPage: React.FC = () => {
  const { taskId } = useParams<{ taskId: string }>();
  const [task, setTask] = useState<Task | null>(null);
  const [config, setConfig] = useState<ActiveLearningConfig>({
    strategy: 'uncertainty',
    band: { min: 0.45, max: 0.65 },
    dailyBudget: 150,
    recentBoostDays: 7,
    enabled: false
  });
  const [lastRun, setLastRun] = useState<ActiveLearningLastRun | null>(null);
  const [previewCandidates, setPreviewCandidates] = useState<Candidate[]>([]);
  const [totalAvailable, setTotalAvailable] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  // Load task data
  useEffect(() => {
    if (!taskId) return;
    
    const unsubscribe = onSnapshot(doc(db, 'tasks', taskId), (doc) => {
      if (doc.exists()) {
        const taskData = { id: doc.id, ...doc.data() } as Task;
        setTask(taskData);
      }
    });

    return unsubscribe;
  }, [taskId]);

  // Load active learning configuration
  useEffect(() => {
    if (!taskId) return;
    
    // Set a timeout to ensure loading doesn't get stuck
    const timeout = setTimeout(() => {
      console.log('Active learning config load timeout - using defaults');
      setLoading(false);
    }, 5000);
    
    const unsubscribe = onSnapshot(doc(db, 'activeLearning', taskId), (doc) => {
      clearTimeout(timeout);
      if (doc.exists()) {
        const data = doc.data();
        if (data?.config) {
          setConfig(data.config);
        }
        if (data?.lastRun) {
          setLastRun(data.lastRun);
        }
      } else {
        // No active learning config exists yet - use defaults
        console.log('No active learning config found for task:', taskId, '- using defaults');
      }
      setLoading(false);
    }, (error) => {
      clearTimeout(timeout);
      console.error('Error loading active learning config:', error);
      setLoading(false);
    });

    return () => {
      clearTimeout(timeout);
      unsubscribe();
    };
  }, [taskId]);

  const handleSaveConfig = async () => {
    if (!taskId) return;
    
    setSaving(true);
    try {
      await setDoc(doc(db, 'activeLearning', taskId), {
        config,
        updatedAt: new Date()
      }, { merge: true });
      alert('Configuration saved successfully!');
    } catch (error) {
      console.error('Error saving config:', error);
      alert('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    if (!taskId) return;
    
    setPreviewing(true);
    try {
      const result = await previewActiveLearning({ taskId, limit: 20 });
      setPreviewCandidates(result.candidates);
      setTotalAvailable(result.totalAvailable);
    } catch (error) {
      console.error('Error previewing:', error);
      alert('Failed to preview candidates');
    } finally {
      setPreviewing(false);
    }
  };

  const handleRunNow = async () => {
    if (!taskId) return;
    
    setRunning(true);
    try {
      const result = await runActiveLearning({ taskId });
      alert(`Active learning completed! Selected ${result.selected} candidates and created ${result.enqueued} labeling jobs.`);
      
      // Refresh last run data
      const alDocRef = doc(db, 'activeLearning', taskId);
      const alDoc = await getDoc(alDocRef);
      if (alDoc.exists() && alDoc.data()?.lastRun) {
        setLastRun(alDoc.data().lastRun);
      }
    } catch (error) {
      console.error('Error running active learning:', error);
      alert('Failed to run active learning');
    } finally {
      setRunning(false);
    }
  };

  const formatAge = (age: number) => {
    if (age < 1) return `${Math.round(age * 24)}h`;
    if (age < 7) return `${Math.round(age)}d`;
    return `${Math.round(age / 7)}w`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading Active Learning data...</div>
      </div>
    );
  }

  if (!taskId) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-400 text-6xl mb-4">⚠️</div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Task ID Required</h3>
        <p className="text-gray-500">Please select a task to configure active learning.</p>
      </div>
    );
  }

  if (!task) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-400 text-6xl mb-4">❌</div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Task Not Found</h3>
        <p className="text-gray-500">The requested task could not be found.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Active Learning</h1>
          <p className="text-gray-600">Task: {task.title}</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={handlePreview}
            disabled={previewing}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {previewing ? 'Previewing...' : 'Preview Selection'}
          </button>
          <button
            onClick={handleRunNow}
            disabled={running || !config.enabled}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            {running ? 'Running...' : 'Run Now'}
          </button>
        </div>
      </div>

      {/* Configuration */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Configuration</h3>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Strategy
              </label>
              <select
                value={config.strategy}
                onChange={(e) => setConfig(prev => ({
                  ...prev,
                  strategy: e.target.value as 'uncertainty' | 'class_balance'
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="uncertainty">Uncertainty Sampling</option>
                <option value="class_balance">Class Balance</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Uncertainty Band
              </label>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-gray-500">Min Score</label>
                  <input
                    type="number"
                    min="0"
                    max="1"
                    step="0.01"
                    value={config.band.min}
                    onChange={(e) => setConfig(prev => ({
                      ...prev,
                      band: { ...prev.band, min: parseFloat(e.target.value) }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500">Max Score</label>
                  <input
                    type="number"
                    min="0"
                    max="1"
                    step="0.01"
                    value={config.band.max}
                    onChange={(e) => setConfig(prev => ({
                      ...prev,
                      band: { ...prev.band, max: parseFloat(e.target.value) }
                    }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Daily Budget
              </label>
              <input
                type="number"
                min="1"
                max="1000"
                value={config.dailyBudget}
                onChange={(e) => setConfig(prev => ({
                  ...prev,
                  dailyBudget: parseInt(e.target.value)
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Recent Boost (days)
              </label>
              <input
                type="number"
                min="1"
                max="30"
                value={config.recentBoostDays}
                onChange={(e) => setConfig(prev => ({
                  ...prev,
                  recentBoostDays: parseInt(e.target.value)
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="enabled"
                checked={config.enabled}
                onChange={(e) => setConfig(prev => ({
                  ...prev,
                  enabled: e.target.checked
                }))}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="enabled" className="ml-2 text-sm text-gray-700">
                Enable Active Learning
              </label>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded p-4">
              <h4 className="text-sm font-medium text-blue-900 mb-2">How it works</h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Selects submissions in the uncertainty band</li>
                <li>• Prioritizes recent submissions</li>
                <li>• Creates high-priority labeling jobs</li>
                <li>• Runs daily when enabled</li>
              </ul>
            </div>

            <button
              onClick={handleSaveConfig}
              disabled={saving}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Configuration'}
            </button>
          </div>
        </div>
      </div>

      {/* History */}
      {lastRun && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Last Run</h3>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{lastRun.selected}</div>
              <div className="text-sm text-gray-600">Selected</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{lastRun.enqueued}</div>
              <div className="text-sm text-gray-600">Enqueued</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">
                {lastRun.ranAt?.toDate?.()?.toLocaleDateString() || 'Unknown'}
              </div>
              <div className="text-sm text-gray-600">Date</div>
            </div>
          </div>
        </div>
      )}

      {/* Preview Table */}
      {previewCandidates.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Preview Selection</h3>
            <div className="text-sm text-gray-600">
              Showing {previewCandidates.length} of {totalAvailable} available
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Submission
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    AI Score
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Age
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Reason
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {previewCandidates.map((candidate) => (
                  <tr key={candidate.submissionId}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {candidate.mediaUrl && (
                          <img
                            src={candidate.mediaUrl}
                            alt="Preview"
                            className="h-10 w-10 object-cover rounded border"
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = '/placeholder-image.jpg';
                            }}
                          />
                        )}
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">
                            {candidate.submissionId.substring(0, 12)}...
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {(candidate.aiScore * 100).toFixed(1)}%
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatAge(candidate.age)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {candidate.reason}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {previewCandidates.length === 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="text-center py-8">
            <div className="text-gray-400 text-4xl mb-2">🔍</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Preview Available</h3>
            <p className="text-gray-500 mb-4">
              Click "Preview Selection" to see candidates for active learning.
            </p>
            <button
              onClick={handlePreview}
              disabled={previewing}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {previewing ? 'Previewing...' : 'Preview Selection'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActiveLearningPage;

